import { camelizeKeys, decamelizeKeys } from 'humps';
import { omit } from 'lodash-es';

type OrderPayload = Record<string, unknown>;
type ProductDetails = Record<string, unknown>;
type ConvertKeys = (value: unknown) => unknown;

// Don't convert all-uppercase or numeric keys — those are product names such as AM_VISION.
const keepShoutingKeys = (key: string, convert: (key: string) => string): string =>
  /^[A-Z0-9_]+$/.test(key) ? key : convert(key);

// iFIT and FACILITY360 have always crossed the API boundary completely untouched, values
// included: the order email keys its column labels off their camelCase field names
// (backend/src/email/renderEmail.ts -> 'newTouchOrTouchXL').
const VERBATIM_SUBTREES: readonly string[] = ['iFIT', 'FACILITY360'];

// AM_VISION is keyed by facility name too, so humps used to mangle those keys on the round trip
// ("Gym A" saved as "gym _a", reloaded as "gymA") and a resumed order lost its console counts
// (DO-295). Its *values* must keep being converted, though, for two reasons:
//   - the nested `pricing` line the order email reads as `list_price`/`promo_label`;
//   - orders stored before this fix hold snake_case inner keys, which the read path must repair.
// So preserve the top-level keys only, and convert everything beneath them as usual.
const KEY_ONLY_SUBTREES: readonly string[] = ['AM_VISION'];

const presentIn = (details: ProductDetails, names: readonly string[]): string[] =>
  names.filter(name => !!details[name]);

// Converts the values of a facility-keyed subtree while carrying its top-level keys across
// untouched, so humps can't mangle facility names like "Gym A".
const convertValuesOnly = (subtree: ProductDetails, convertKeys: ConvertKeys): ProductDetails =>
  Object.keys(subtree).reduce<ProductDetails>((memo, key) => {
    memo[key] = convertKeys(subtree[key]);
    return memo;
  }, {});

const convertOrder = (
  order: object,
  sourceKey: 'productDetails' | 'product_details',
  targetKey: 'productDetails' | 'product_details',
  convertKeys: ConvertKeys,
): OrderPayload => {
  const payload = order as OrderPayload;
  const details = payload[sourceKey] as ProductDetails | undefined;
  if (!details) {
    return convertKeys(payload) as OrderPayload;
  }

  const verbatim = presentIn(details, VERBATIM_SUBTREES);
  const keyOnly = presentIn(details, KEY_ONLY_SUBTREES);
  const converted = convertKeys({
    ...payload,
    [sourceKey]: omit(details, [...verbatim, ...keyOnly]),
  }) as OrderPayload;

  const convertedDetails = converted[targetKey] as ProductDetails;
  verbatim.forEach(name => {
    convertedDetails[name] = details[name];
  });
  keyOnly.forEach(name => {
    convertedDetails[name] = convertValuesOnly(details[name] as ProductDetails, convertKeys);
  });
  return converted;
};

// Both take `object` rather than a concrete order type: they are also handed raw API responses,
// whose snake_case shape has no interface, and DigitalOrdersBase has no index signature.
export const prepareOrder = (payload: object): OrderPayload =>
  convertOrder(payload, 'productDetails', 'product_details', value =>
    decamelizeKeys(value, keepShoutingKeys),
  );

export const mapOrder = (payload: object): OrderPayload =>
  convertOrder(payload, 'product_details', 'productDetails', value =>
    camelizeKeys(value, keepShoutingKeys),
  );
